/**
 * Extracts a Google Drive file ID from various forms of Google Drive URLs.
 * 
 * Supports URLs like:
 * - https://drive.google.com/file/d/1abc123.../view
 * - https://docs.google.com/document/d/1abc123.../edit
 * - https://docs.google.com/spreadsheets/d/1abc123.../edit
 * - https://docs.google.com/presentation/d/1abc123.../edit
 * 
 * @param url The Google Drive URL
 * @returns The extracted file ID or null if not found
 */
export const extractGoogleDriveFileId = (url: string): string | null => {
  if (!url || typeof url !== "string") return null;

  try {
    // Basic ID regex: alphanumeric characters, hyphens, and underscores, exactly 33 chars long usually, but can vary
    const idRegex = /[-\w]{25,}/;

    // Pattern 1: drive.google.com/file/d/ID/...
    const driveMatch = url.match(/\/file\/d\/([-\w]{25,})/);
    if (driveMatch && driveMatch[1]) {
      return driveMatch[1];
    }

    // Pattern 2: docs.google.com/.../d/ID/...
    const docsMatch = url.match(/\/(?:document|spreadsheets|presentation)\/d\/([-\w]{25,})/);
    if (docsMatch && docsMatch[1]) {
      return docsMatch[1];
    }

    // Pattern 3: Using 'id' query parameter e.g., ?id=ID
    const urlObj = new URL(url);
    const idParam = urlObj.searchParams.get("id");
    if (idParam && idRegex.test(idParam)) {
      return idParam;
    }

    // Pattern 4: direct ID pasted
    if (idRegex.test(url) && !url.includes("/")) {
      return url.trim();
    }

    return null;
  } catch (e) {
    // Handle invalid URL instantiation gracefully if needed
    console.error("Error parsing Google Drive URL:", e);
    return null;
  }
};
